import { useLayoutEffect, useRef, useState } from "react";
import { Popover, Tag } from "antd";
import { fitChipCount } from "@mappers/notes.mapper.mjs";

// Keep in step with the gap of `.fitted-tags`.
const CHIP_GAP = 4;

interface FittedTagsProps {
    tags: string[];
    /** The most chips to show, even when more fit. */
    max?: number;
    /** Sets the size and the place of the chips for each use. */
    className: string;
}

/** Tag chips on one line. The chips that do not fit go in a "+N" chip, and a hover on it shows them. */
export const FittedTags = ({ tags, max = Infinity, className }: FittedTagsProps) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(Math.min(tags.length, max));

    // A hidden copy of every chip, plus a "+N" chip, gives the widths. The box width gives the space.
    useLayoutEffect(() => {
        const box = boxRef.current;
        const measure = measureRef.current;
        if (!box || !measure) return;
        const fit = () => {
            const widths = Array.from(measure.children, (chip) => (chip as HTMLElement).offsetWidth);
            const restWidth = widths.pop() ?? 0;
            setVisible(fitChipCount(widths, restWidth, CHIP_GAP, box.clientWidth, max));
        };
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(box);
        return () => observer.disconnect();
    }, [tags, max]);

    if (tags.length === 0) return null;

    const hidden = tags.slice(visible);
    return (
        <div ref={boxRef} className={`fitted-tags ${className}`}>
            <div ref={measureRef} className="fitted-tags-measure" aria-hidden>
                {tags.map((tag) => (
                    <Tag key={tag} bordered={false} color="blue">
                        {tag}
                    </Tag>
                ))}
                <Tag bordered={false} color="blue">
                    +{tags.length}
                </Tag>
            </div>
            {tags.slice(0, visible).map((tag) => (
                <Tag key={tag} bordered={false} color="blue">
                    {tag}
                </Tag>
            ))}
            {hidden.length > 0 && (
                <Popover
                    content={
                        <div className="fitted-tags-more">
                            {hidden.map((tag) => (
                                <Tag key={tag} bordered={false} color="blue">
                                    {tag}
                                </Tag>
                            ))}
                        </div>
                    }
                >
                    <Tag bordered={false} color="blue">
                        +{hidden.length}
                    </Tag>
                </Popover>
            )}
        </div>
    );
};
